import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

client.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("uat_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("uat_token");
      localStorage.removeItem("uat_user");
      if (!window.location.pathname.startsWith("/login")) window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

export const api = {
  // auth
  login: (u, p) => client.post("/auth/login", { username: u, password: p }),
  me: () => client.get("/auth/me"),
  changePassword: (old_password, new_password) =>
    client.post("/auth/change-password", { old_password, new_password }),

  // users
  listUsers: () => client.get("/users"),
  createUser: (b) => client.post("/users", b),
  updateUser: (id, b) => client.patch(`/users/${id}`, b),
  deleteUser: (id) => client.delete(`/users/${id}`),

  // locations
  listLocations: () => client.get("/locations"),
  createLocation: (b) => client.post("/locations", b),
  updateLocation: (id, b) => client.patch(`/locations/${id}`, b),
  deleteLocation: (id) => client.delete(`/locations/${id}`),

  // areas
  listAreas: (params) => client.get("/areas", { params }),
  createArea: (b) => client.post("/areas", b),
  updateArea: (id, b) => client.patch(`/areas/${id}`, b),
  deleteArea: (id) => client.delete(`/areas/${id}`),

  // categories
  listCategories: () => client.get("/categories"),
  createCategory: (b) => client.post("/categories", b),
  updateCategory: (code, b) => client.patch(`/categories/${code}`, b),
  deleteCategory: (code) => client.delete(`/categories/${code}`),

  // assets
  listAssets: (params) => client.get("/assets", { params }),
  getAsset: (id) => client.get(`/assets/${id}`),
  createAsset: (b) => client.post("/assets", b),
  updateAsset: (id, b) => client.patch(`/assets/${id}`, b),
  deleteAsset: (id) => client.delete(`/assets/${id}`),

  // maintenance
  listMaintenance: (assetId) => client.get(`/assets/${assetId}/maintenance`),
  addMaintenance: (assetId, b) => client.post(`/assets/${assetId}/maintenance`, b),
  updateMaintenance: (logId, b) => client.patch(`/maintenance/${logId}`, b),
  deleteMaintenance: (logId) => client.delete(`/maintenance/${logId}`),
  recentMaintenance: (limit = 10) => client.get("/maintenance/recent", { params: { limit } }),
  listAllMaintenance: (params) => client.get("/maintenance", { params }),
  exportMaintenance: (params) =>
    client.get("/maintenance/export", { params, responseType: "blob" }),

  // maintenance attachments
  uploadMaintenanceAttachments: (logId, files) => {
    const fd = new FormData();
    files.forEach(f => fd.append("files", f));
    return client.post(`/maintenance/${logId}/attachments`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  downloadAttachment: (attachmentId) =>
    client.get(`/attachments/${attachmentId}`, { responseType: "blob" }),
  deleteAttachment: (attachmentId) => client.delete(`/attachments/${attachmentId}`),

  // asset-level attachments (reuse maintenance_attachments with log_id:null)
  listAssetAttachments: (assetId) => client.get(`/assets/${assetId}/attachments`),
  uploadAssetAttachments: (assetId, files) => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    return client.post(`/assets/${assetId}/attachments`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // insurance history (vehicles)
  listInsurance: (assetId) => client.get(`/assets/${assetId}/insurance`),
  addInsurance: (assetId, b) => client.post(`/assets/${assetId}/insurance`, b),
  updateInsurance: (id, b) => client.patch(`/insurance/${id}`, b),
  deleteInsurance: (id) => client.delete(`/insurance/${id}`),
  uploadInsuranceAttachments: (id, files) => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    return client.post(`/insurance/${id}/attachments`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  exportInsurance: (assetId) =>
    client.get("/insurance/export", { params: { asset_id: assetId }, responseType: "blob" }),

  // conditions
  listConditions: (assetId) => client.get(`/assets/${assetId}/conditions`),
  addCondition: (assetId, b) => client.post(`/assets/${assetId}/conditions`, b),

  // dashboard
  dashboardStats: () => client.get("/dashboard/stats"),
  expiring: (days = 30) => client.get("/dashboard/expiring", { params: { days } }),

  // reports
  reportSummary: (params) => client.get("/reports/summary", { params }),
  exportReport: (params) => client.get("/reports/export", { params, responseType: "blob" }),
  exportAssetRegister: (params) =>
    client.get("/reports/assets/export", { params, responseType: "blob" }),

  // audit
  audit: (limit = 200) => client.get("/audit", { params: { limit } }),

  // backup
  backupExport: () => client.get("/backup/export", { responseType: "blob" }),
  backupRestore: (file, replace = true) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/backup/restore?replace=${replace}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  backupList: () => client.get("/backup/list"),

  // import
  importExcel: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/import/excel", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // Bulk move
  bulkMove: (asset_ids, target_location_id) =>
    client.post("/assets/bulk-move", { asset_ids, target_location_id }),

  // QR codes
  publicScan: (assetIdStr) => client.get(`/public/scan/${encodeURIComponent(assetIdStr)}`),
  getAssetByCode: (code) => client.get(`/assets/by-code/${encodeURIComponent(code)}`),
  qrPngUrl: (assetId) => {
    const base = encodeURIComponent(window.location.origin);
    return `${API}/qr/asset/${assetId}.png?base_url=${base}`;
  },
  qrSheetUrl: ({ category_code, location_id }) => {
    const params = new URLSearchParams();
    if (category_code) params.set("category_code", category_code);
    if (location_id) params.set("location_id", location_id);
    params.set("base_url", window.location.origin);
    params.set("t", localStorage.getItem("uat_token") || "");
    return `${API}/qr/sheet?${params.toString()}`;
  },
};

export const getUser = () => {
  try { return JSON.parse(localStorage.getItem("uat_user") || "null"); } catch { return null; }
};
export const setSession = (token, user) => {
  localStorage.setItem("uat_token", token);
  localStorage.setItem("uat_user", JSON.stringify(user));
};
export const clearSession = () => {
  localStorage.removeItem("uat_token");
  localStorage.removeItem("uat_user");
};

export const isAdmin = (u = getUser()) => u?.role === "admin";
export const canEditAtLocation = (u, locationId) => {
  if (!u) return false;
  if (u.role === "admin") return true;
  if (u.role === "location_editor") return u.location_id === locationId;
  return false;
};
